import { type HTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import { Button, type ButtonVariant } from './Button.js';
import './ResultBanner.css';

export interface SubmitBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** CTA label. Default Georgian "შემოწმება" (check). */
  ctaLabel?: ReactNode;
  ctaVariant?: ButtonVariant;
  ctaDisabled?: boolean;
  ctaLoading?: boolean;
  onCta?: () => void;
  /** Optional ghost button on the left (e.g. "Skip"). */
  secondaryLabel?: ReactNode;
  onSecondary?: () => void;
  ref?: Ref<HTMLDivElement>;
}

/**
 * SubmitBar — pre-check footer. Sticky at the bottom of an exercise,
 * holds the primary "Check" button (disabled until the user has
 * provided an answer) and an optional ghost secondary (skip / help).
 *
 * Visually it's the neutral state of ResultBanner — shares the CSS
 * so when the user hits Check and a result arrives, the layout
 * swap is seamless.
 */
export function SubmitBar({
  ctaLabel,
  ctaVariant = 'primary',
  ctaDisabled,
  ctaLoading,
  onCta,
  secondaryLabel,
  onSecondary,
  className,
  ref,
  ...rest
}: SubmitBarProps) {
  return (
    <div
      ref={ref}
      className={clsx('fluent-resultbar', className)}
      data-state="neutral"
      {...rest}
    >
      <div className="fluent-resultbar__cta">
        {secondaryLabel != null && (
          <Button variant="ghost" size="md" onClick={onSecondary}>
            {secondaryLabel}
          </Button>
        )}
        <Button
          variant={ctaVariant}
          size="md"
          onClick={onCta}
          disabled={ctaDisabled}
          loading={ctaLoading}
        >
          {ctaLabel ?? 'შემოწმება'}
        </Button>
      </div>
    </div>
  );
}
